import { useState, useRef, useEffect, useCallback } from 'react';
import { _t } from '@/i18n/t';
import MarcoAvatar from './MarcoAvatar';

interface SupportAssistantBubbleProps {
  onClick: () => void;
  onDismiss: () => void;
  showLabel?: boolean;
}

const SNAP_BACK_DELAY = 3000;
const DRAG_THRESHOLD = 5;
const EDGE_MARGIN = 8;
const ENABLE_SNAP_BACK = false;

const SupportAssistantBubble = ({ onClick, showLabel = true }: SupportAssistantBubbleProps) => {
  const [mounted, setMounted] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const bubbleRef = useRef<HTMLDivElement>(null);
  const snapBackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const drag = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
    homeRect: null as DOMRect | null,
  });

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const clearSnapBack = useCallback(() => {
    if (snapBackTimer.current) {
      clearTimeout(snapBackTimer.current);
      snapBackTimer.current = null;
    }
  }, []);

  useEffect(() => clearSnapBack, [clearSnapBack]);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    clearSnapBack();
    const d = drag.current;
    d.pointerId = e.pointerId;
    d.startX = e.clientX;
    d.startY = e.clientY;
    d.originX = offset.x;
    d.originY = offset.y;
    d.moved = false;
    d.homeRect = bubbleRef.current?.getBoundingClientRect() ?? null;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (d.pointerId !== e.pointerId) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    if (!isDragging) setIsDragging(true);

    let nextX = d.originX + dx;
    let nextY = d.originY + dy;

    const rect = d.homeRect;
    if (rect) {
      const minX = EDGE_MARGIN - rect.left + d.originX;
      const maxX = window.innerWidth - EDGE_MARGIN - rect.right + d.originX;
      const minY = EDGE_MARGIN - rect.top + d.originY;
      const maxY = window.innerHeight - EDGE_MARGIN - rect.bottom + d.originY;
      nextX = Math.min(Math.max(nextX, minX), maxX);
      nextY = Math.min(Math.max(nextY, minY), maxY);
    }
    setOffset({ x: nextX, y: nextY });
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (d.pointerId !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    d.pointerId = -1;

    if (d.moved) {
      setIsDragging(false);
      setOffset((o) => ({ x: 0, y: o.y }));

      if (ENABLE_SNAP_BACK) {
        clearSnapBack();
        snapBackTimer.current = setTimeout(() => {
          setOffset({ x: 0, y: 0 });
        }, SNAP_BACK_DELAY);
      }
    }
  };

  const handleClick = () => {
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    onClick();
  };

  return (
    <div
      ref={bubbleRef}
      className="fixed bottom-6 right-6 z-40"
      style={{
        transform: mounted
          ? `translate(${offset.x}px, ${offset.y}px) scale(1)`
          : 'translate(0px, 16px) scale(0.9)',
        opacity: mounted ? 1 : 0,
        transition: isDragging
          ? 'none'
          : 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease',
        willChange: 'transform',
        touchAction: 'none', // stop the page from scrolling while dragging on touch
      }}
    >
      {showLabel && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-full right-0 mb-3 whitespace-nowrap rounded-[10px] bg-foreground px-3 py-2 text-[13px] font-medium text-background shadow-lg after:absolute after:right-[20px] after:top-full after:border-[6px] after:border-b-0 after:border-transparent after:border-t-foreground after:content-['']"
        >
          {_t('assistant.launcher.label')}
        </span>
      )}

      <button
        type="button"
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="group flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-full shadow-lg outline outline-2 outline-foreground transition-shadow duration-200 hover:shadow-xl"
        style={{
          cursor: isDragging ? 'grabbing' : 'pointer',
          touchAction: 'none',
        }}
        title={_t('assistant.launcher.label')}
      >
        <MarcoAvatar className="h-full w-full transition-transform duration-200 group-hover:scale-110" />
      </button>
    </div>
  );
};

export default SupportAssistantBubble;